import { keyBy } from 'lodash';
import { CareTeamAction, CareTeamDataState, CareTeamState } from './types';
import {
  CareTeamMember,
  CaseManagerDetails,
  Channel,
  Contact,
  Message,
  User,
} from '../type';
import produce from 'immer';

export const careTeamReducer = (
  currentState: CareTeamState,
  action: CareTeamAction,
): CareTeamState => {
  console.debug('action', action, currentState);
  const actionType = action.type;
  if (actionType === 'INIT') {
    return {
      currentUserGuid: action.currentUserGuid,
      patientCareTeamMembers: null,
      patientContacts: {},
      ccmDetails: {},
      patients: {},
      channels: {},
      users: {},
      messages: {},
      mediaMessages: {},
      rawFileMessages: {},
      channelMetadata: {},
      fileUploads: {},
    };
  }

  if (currentState === 'LOADING') {
    return currentState;
  }

  if (actionType === 'CARE_TEAM_MEMBER_UPDATE') {
    return produce(currentState, (draft) => {
      draft.patientCareTeamMembers = action.patientCareTeamMembers.reduce(
        (result, member) => {
          result[member.patientGuid] = member.careTeamMembers;
          return result;
        },
        {} as { [patientGuid: string]: CareTeamMember[] },
      );
    });
  }
  if (actionType === 'PATIENT_CONTACTS_UPDATE') {
    return produce(currentState, (draft) => {
      draft.patientContacts = action.patientContacts.reduce(
        (result, patientContact) => {
          result[patientContact.patientGuid] = patientContact.contacts;
          return result;
        },
        {} as { [patientGuid: string]: Contact[] },
      );
    });
  }
  if (actionType === 'CCM_DETAILS_UPDATE') {
    return produce(currentState, (draft) => {
      draft.ccmDetails = action.ccmDetails.reduce((result, ccm) => {
        for (const patientGuid of ccm.assignedPatientGuids) {
          const ccmList = result[patientGuid] ?? [];
          ccmList.push(ccm);
          result[patientGuid] = ccmList;
        }
        return result;
      }, {} as { [patientGuid: string]: CaseManagerDetails[] });
    });
  } else if (actionType === 'PATIENT_UPDATE') {
    return produce(currentState, (draft) => {
      draft.patients = keyBy(action.patients, (patient) => patient.guid);
    });
  } else if (actionType === 'CHANNEL_UPDATE') {
    return produce(currentState, (draft) => {
      const { channels, users } = action;
      const filteredChannels = channels.filter((channel) => {
        const { readonly, type } = channel;
        return !(readonly && type === 'SCHEDULING_TEAM');
      });
      draft.channels = mergeChannels(draft, filteredChannels);
      draft.users = mergeUsers(draft, users);
    });
  } else if (actionType === 'MESSAGE_LOADED') {
    return produce(currentState, (draft) => {
      const { channelGuid, messages, hasMore, users } = action;
      const channelMetadata = draft.channelMetadata[channelGuid] ?? {};
      channelMetadata.hasMore = hasMore;
      draft.channelMetadata[channelGuid] = channelMetadata;
      draft.messages[channelGuid] = mergeMessages(
        draft.messages[channelGuid],
        messages,
      );
      draft.users = mergeUsers(draft, users);
    });
  } else if (actionType === 'FILE_MESSAGE_LOADED') {
    return produce(currentState, (draft) => {
      const { channelGuid, fileUploadType, messages, hasMore } = action;
      const channelMetadata = draft.channelMetadata[channelGuid] ?? {};
      channelMetadata.hasMore = hasMore;
      const field =
        fileUploadType === 'MEDIA' ? 'mediaMessages' : 'rawFileMessages';
      draft[field][channelGuid] = mergeMessages(
        draft[field][channelGuid],
        messages,
      );
      draft.channelMetadata[channelGuid] = channelMetadata;
    });
  } else if (actionType === 'MESSAGE_UPDATE') {
    return produce(currentState, (draft) => {
      const { channelGuid, newMessages, updatedMessages, users } = action;
      // update all messages store
      const changedMessages = [...newMessages, ...updatedMessages];
      draft.messages[channelGuid] = mergeMessages(
        draft.messages[channelGuid],
        changedMessages,
      );
      // update media message store
      const newMediaMessages = changedMessages.filter((message) => {
        const { messageType, scanStatus } = message;
        return (
          (['IMAGE', 'VIDEO'].includes(messageType) &&
            scanStatus === 'CLEAN') ||
          message.mediaDeleted
        );
      });
      draft.mediaMessages[channelGuid] = mergeFileMessages(
        draft.mediaMessages[channelGuid],
        newMediaMessages,
      );
      // update raw file message store
      const newRawFileMessages = changedMessages.filter((message) => {
        const { messageType, scanStatus } = message;
        return (
          (['PDF', 'WORD'].includes(messageType) && scanStatus === 'CLEAN') ||
          message.mediaDeleted
        );
      });
      draft.rawFileMessages[channelGuid] = mergeFileMessages(
        draft.rawFileMessages[channelGuid],
        newRawFileMessages,
      );

      draft.users = mergeUsers(draft, users);
    });
  } else if (actionType === 'FILE_UPLOAD') {
    return produce(currentState, (draft) => {
      const { message, state, progress } = action;
      const { channelGuid, guid: messageGuid } = message;
      const fileUploads = draft.fileUploads[channelGuid] ?? {};
      if (state === 'canceled') {
        delete fileUploads[messageGuid];
      } else {
        fileUploads[messageGuid] = { message, state, progress };
      }
      draft.fileUploads[channelGuid] = fileUploads;
    });
  }

  return currentState;
};

const mergeChannels = (
  store: CareTeamDataState,
  channels: Channel[],
): { [channelGuid: string]: Channel } => {
  const clonedChannels = { ...store.channels };
  for (const channel of channels) {
    clonedChannels[channel.guid] = channel;
  }
  return clonedChannels;
};

const mergeUsers = (
  store: CareTeamDataState,
  users: User[],
): { [userGuid: string]: User } => {
  const clonedUsers = { ...store.users };
  for (const user of users) {
    clonedUsers[user.guid] = user;
  }
  return clonedUsers;
};

const mergeMessages = (
  currentMessages: Message[] | undefined,
  messages: Message[],
): Message[] => {
  if (!messages.length) {
    return currentMessages ?? [];
  }
  const currentMessageByGuid = keyBy(
    currentMessages ?? [],
    (message) => message.guid,
  );
  for (const message of messages) {
    currentMessageByGuid[message.guid] = message;
  }
  return Object.values(currentMessageByGuid).sort((m1, m2) => {
    return m1.createdDate > m2.createdDate ? -1 : 1;
  });
};

const mergeFileMessages = (
  currentMessages: Message[] | undefined,
  messages: Message[],
): Message[] => {
  if (!messages.length) {
    return currentMessages ?? [];
  }
  const currentMessageByGuid = keyBy(
    currentMessages ?? [],
    (message) => message.guid,
  );
  for (const message of messages) {
    if (message.mediaDeleted) {
      delete currentMessageByGuid[message.guid];
    } else {
      currentMessageByGuid[message.guid] = message;
    }
  }
  return Object.values(currentMessageByGuid).sort((m1, m2) => {
    return m1.createdDate > m2.createdDate ? -1 : 1;
  });
};
